import {
    BidCarsPlanningSettingsModel,
    BidRefuelingServiceType,
    BooksEditPermissionsFlag,
    CurrencyType,
    DriverControlPointsSettingsModel,
    DriverEducationResourceSettingsModel,
    ExploitationSettingsModel,
    ExpressBidSettingsJsonObject,
    OrganizationDefaultRouteOptionsModel,
    OrganizationFlags,
    OrganizationProfileModel,
    OrganizationReportExecutionModel,
    ProfitabilityOfBidSettingsModel,
    ReportExecutionTimeModel,
    RouterProfile,
    RouterType,
    SyncSystemModel,
    TimezoneViewModel,
    UserNotificationsModel,
} from "api";
import actionTypes from "constants/profile";
import { defaultChatInitializeTypesEnum, driverChatInitializeTypesEnum } from "helpers/enums";
import { IUserModel } from "helpers/models/user";
import { createRolesObject, RolesModel } from "helpers/roles";
import { Action, handleActions } from "redux-actions";

type RefuelingSettingsModel = {
    addCarToRefuelingSyncByDefault: boolean;
    addTrailerToRefuelingSyncByDefault: boolean;
    hideRefuelingPlanning: boolean;
};

const {
    GET_INITIAL_PROFILE,
    GET_USER,
    GET_ORG,
    GET_WORK_REST_INFO,
    GET_NOTIFICATION_OPTIONS,
    GET_TIMEZONE,
    GET_DEFAULT_ROUTE_OPTIONS,
    GET_REPORT_EXECUTION_TIMES,
    GET_CONTROL_POINTS_SETTINGS,
    GET_SYNC_SYSTEMS,
    GET_PROFITABILITY_OF_BID_SETTINGS,
    GET_DRIVER_EDUCATION_RESOURCE_SETTINGS,
    GET_BID_CARS_PLANNING_SETTINGS,
    GET_CHATS_SETTINGS,
    GET_REFUELING_SETTINGS,
    GET_EXPLOITATION_SETTINGS,
} = actionTypes;

const storedToken = localStorage.getItem("token");
const storedCurrentUser = localStorage.getItem("currentUser")
    ? JSON.parse(localStorage.getItem("currentUser") || "")
    : false;

const initialState: State = {
    token: storedToken || "",
    roles: createRolesObject(storedCurrentUser?.roles || []),
    initialProfileLoaded: false,
    organizationLoaded: false,
    profile: storedCurrentUser || {
        fullName: "",
        imageUrl: "",
        isFuelLimitEnabled: false,
    },
    notificationOptions: { notificationOptions: [], doNotSendNotificationsAtNight: false },
    organization: {
        name: "",
        email: "",
        site: "",
        phoneNumber: "",
        address: "",
        ogrn: "",
        bank: "",
        ks: "",
        imageUrl: "",
        flags: OrganizationFlags.None,
        booksEditPermissionsFlags: BooksEditPermissionsFlag.None,
        intBooksEditPermissionsFlags: 0,
        intFlags: 0,
        currency: CurrencyType.RUB,
        isDriverGeozoneControlEnabled: false,
        noAccessToEditBooks: false,
        closedPeriodIsEnabled: false,
        criticalMileage: 0,
        usesBidsApproval: false,
        bidPointVisitIgnoreDuration: 0,
        geozoneRadius: 0,
        suppressRefuelingUpdateRadius: 0,
        driverAtPointOffset: 0,
        minimumBidItemChangePointDuration: "",
        minimumCarStopDuration: "",
        minimumRefuelingDuration: 0,
        startBidWithSuccessSync: false,
        startEmptyMileageBidWithoutSync: false,
        refuelingServiceType: BidRefuelingServiceType.None,
        routerType: RouterType.OSRM,
        displayPlanLeaveDate: false,
        weightLimit: false,
        maxPlanMileageDeviation: 0,
        sendMobileAppCoordinates: false,
        sendCoordinatesGap: 0,
        saveCoordinatesInterval: 0,
        onlyNewPhotos: false,
        id: 0,
    },
    timezone: {
        id: "",
        displayName: "",
        utcOffset: "",
    },
    workRestInfo: {
        dayStart: "08:00",
        averageSpeed: 0,
        workHours: 0,
        emptyMileagePointStayPeriod: undefined,
        useLastCarLocationForEmptyMileageBid: false,
    },
    controlPointsSettings: {
        checkVisitingTime: "",
        checkLatenessTime: "",
        planDailyMileage: 0,
        customPointTypeIds: undefined,
        maxMileageAfterCustomPoint: 0,
    },
    defaultRouteOptions: {
        routerType: RouterType.OSRM,
        disallowToll: false,
        disallowTollExceptM4: false,
        disallowTollExceptM12DRT: false,
        avoidTollRoadFlags: 0,
        avoidSpecialRoadFlags: 0,
        allowFerry: false,
        calcTolls: false,
        useIndividualAccount: false,
        apiKey: undefined,
        country: undefined,
        routerProfile: RouterProfile.HgvOptimal,
        useCargorunRouterOnly: false,
    },
    syncSystems: { syncSystems: [] },
    bidCarsPlanningSettings: { allowableDistanceFromLastBidPoint: undefined },
    chatsSettings: {},
    refuelingSettings: {
        addCarToRefuelingSyncByDefault: false,
        addTrailerToRefuelingSyncByDefault: false,
        hideRefuelingPlanning: true,
    },
    exploitationSettings: {},
    educationResources: { resources: [] },
    executionTimes: [],
    profitabilityOfBidSettings: {
        averageFuelConsumption: 0,
        averageFuelCostPerLiter: 0,
        costOfOneDay: 0,
        hoursToDaysConvertasionsRate: 0,
    },
};

type State = {
    initialProfileLoaded: boolean;
    organizationLoaded: boolean;
    token: string;
    roles: RolesModel;
    profile: IUserModel;
    notificationOptions: UserNotificationsModel;
    organization: OrganizationProfileModel;
    timezone: TimezoneViewModel;
    workRestInfo: ExpressBidSettingsJsonObject;
    controlPointsSettings: DriverControlPointsSettingsModel;
    defaultRouteOptions: OrganizationDefaultRouteOptionsModel;
    syncSystems: { syncSystems: SyncSystemModel[] };
    bidCarsPlanningSettings: BidCarsPlanningSettingsModel;
    chatsSettings: ChatSettingsModel;
    refuelingSettings: RefuelingSettingsModel;
    exploitationSettings: ExploitationSettingsModel;
    executionTimes: ReportExecutionTimeModel[];
    profitabilityOfBidSettings: ProfitabilityOfBidSettingsModel;
    educationResources: DriverEducationResourceSettingsModel;
};

type GetInitialProfilePayload = { token: string; currentUser: IUserModel };
type GetUserPayload = { token: string; currentUser: IUserModel };
type GetSyncSystemPayload = { syncSystems: SyncSystemModel[] };
type ChatSettingsModel = {
    driverChatInitializeType?: keyof typeof driverChatInitializeTypesEnum;
    defaultChatInitializeType?: keyof typeof defaultChatInitializeTypesEnum;
};

type CombinedPayload =
    | GetInitialProfilePayload
    | GetUserPayload
    | TimezoneViewModel
    | OrganizationProfileModel
    | ExpressBidSettingsJsonObject
    | OrganizationDefaultRouteOptionsModel
    | OrganizationReportExecutionModel
    | UserNotificationsModel
    | ProfitabilityOfBidSettingsModel
    | DriverControlPointsSettingsModel
    | GetSyncSystemPayload
    | DriverEducationResourceSettingsModel
    | ChatSettingsModel
    | RefuelingSettingsModel
    | ExploitationSettingsModel
    | BidCarsPlanningSettingsModel;

export default handleActions<State, CombinedPayload>(
    {
        [GET_INITIAL_PROFILE]: (state, { payload }: Action<GetInitialProfilePayload>) => {
            const roles = createRolesObject(payload?.currentUser?.roles);
            if (roles.isTruckingOrganizationDispatcher) {
                localStorage.setItem("isTechSupport", "true");
            }
            return {
                ...state,
                token: payload?.token,
                profile: {
                    ...payload?.currentUser,
                    availableFeatureModules: payload?.currentUser?.availableFeatureModules || [],
                },
                roles,
                initialProfileLoaded: true,
            };
        },
        [GET_USER]: (state, { payload }: Action<GetUserPayload>) => ({
            ...state,
            profile: { ...state.profile, ...payload },
        }),
        [GET_TIMEZONE]: (state, { payload }: Action<TimezoneViewModel>) => ({
            ...state,
            timezone: { ...payload },
        }),
        [GET_ORG]: (state, { payload }: Action<OrganizationProfileModel>) => ({
            ...state,
            organizationLoaded: true,
            organization: { ...payload },
        }),
        [GET_WORK_REST_INFO]: (state, { payload }: Action<ExpressBidSettingsJsonObject>) => ({
            ...state,
            workRestInfo: { ...payload },
        }),
        [GET_DEFAULT_ROUTE_OPTIONS]: (state, { payload }: Action<OrganizationDefaultRouteOptionsModel>) => ({
            ...state,
            defaultRouteOptions: { ...payload },
        }),
        [GET_REPORT_EXECUTION_TIMES]: (state, { payload }: Action<OrganizationReportExecutionModel>) => ({
            ...state,
            executionTimes: payload.executionTimes || [],
        }),
        [GET_NOTIFICATION_OPTIONS]: (state, { payload }: Action<UserNotificationsModel>) => ({
            ...state,
            notificationOptions: payload || { notificationOptions: [], doNotSendNotificationsAtNight: false },
        }),
        [GET_PROFITABILITY_OF_BID_SETTINGS]: (state, { payload }: Action<ProfitabilityOfBidSettingsModel>) => ({
            ...state,
            profitabilityOfBidSettings: { ...payload },
        }),
        [GET_CONTROL_POINTS_SETTINGS]: (state, { payload }: Action<DriverControlPointsSettingsModel>) => ({
            ...state,
            controlPointsSettings: { ...payload },
        }),
        [GET_SYNC_SYSTEMS]: (state, { payload }: Action<GetSyncSystemPayload>) => ({
            ...state,
            syncSystems: { ...payload },
        }),
        [GET_DRIVER_EDUCATION_RESOURCE_SETTINGS]: (
            state,
            { payload }: Action<DriverEducationResourceSettingsModel>,
        ) => ({
            ...state,
            educationResources: { ...payload },
        }),
        [GET_CHATS_SETTINGS]: (state, { payload }: Action<ChatSettingsModel>) => ({
            ...state,
            chatsSettings: { ...payload },
        }),
        [GET_REFUELING_SETTINGS]: (state, { payload }: Action<RefuelingSettingsModel>) => ({
            ...state,
            refuelingSettings: { ...payload },
        }),
        [GET_EXPLOITATION_SETTINGS]: (state, { payload }: Action<ExploitationSettingsModel>) => ({
            ...state,
            exploitationSettings: { ...payload },
        }),
        [GET_BID_CARS_PLANNING_SETTINGS]: (state, { payload }: Action<BidCarsPlanningSettingsModel>) => ({
            ...state,
            bidCarsPlanningSettings: { ...payload },
        }),
    },
    initialState,
);
